// src/pages/doctors/appointments/index.jsx
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AvatarThumb68 from "@/sot/primitives/AvatarThumb68.jsx";
import Card from "@/components/ui/Card.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";
import supabase from "@/services/supabaseClient";
import { loadDoctorAppointmentsFeed } from "@/pages/doctors/appointments/lib/doctorAppointmentsData.js";
import { readDoctorConsultancies } from "@/lib/localDoctorStorage";
import { recordStagingPerformance } from "@/lib/stagingPerformanceTrace.js";

/**
 * Doctor Appointments
 * Route target: /doctors/appointments
 * Next: /doctors/appointments/:appointmentId
 */

// Header action icon
const ICON_CALENDAR = "/icons/doctor/appointments/calendar.svg"; // 24x24
const ICON_CALENDAR_TOGGLE = "/icons/doctor/appointments/calendar_toggle.svg"; // 24x24
const ICON_LISTS = "/icons/doctor/appointments/lists.svg"; // 24x24
const ICON_WEEK_VIEW = "/icons/doctor/appointments/calendar-week.svg"; // 24x24
const ICON_MONTH_VIEW = "/icons/doctor/appointments/month_view.svg"; // 24x24
const ICON_MONTH_BACK = "/icons/before.svg";
const ICON_MONTH_FORWARD = "/icons/next.svg";
const ICON_APPOINTMENT_LOCATION = "/icons/doctor/home_content_media/distance.svg";
const ICON_APPOINTMENT_EVISIT = "/icons/list-items/emergency_recording_Fill.svg";
const ICON_APPOINTMENT_PENDING = "/icons/doctor/appointments/Pending.svg";

const MIN_HOUR = 0;
const MAX_HOUR = 23;
const PX_PER_MIN = 1.4; // Figma daily view sits closer to 84px per hour
const TIMELINE_HEIGHT = (MAX_HOUR - MIN_HOUR + 1) * 60 * PX_PER_MIN;
const TIME_RAIL_WIDTH = 64;

function resolveUiLocale(languageCode) {
  if (languageCode === "sn") return "sn-ZW";
  if (languageCode === "zu") return "zu-ZA";
  return languageCode || undefined;
}

const CUSTOM_LOCALE_LABELS = {
  sn: {
    monthsLong: [
      "Ndira",
      "Kukadzi",
      "Kurume",
      "Kubvumbi",
      "Chivabvu",
      "Chikumi",
      "Chikunguru",
      "Nyamavhuvhu",
      "Gunyana",
      "Gumiguru",
      "Mbudzi",
      "Zvita",
    ],
    monthsShort: ["Nd", "Kk", "Km", "Kb", "Cv", "Ck", "Cg", "Ny", "Gu", "Gm", "Mb", "Zv"],
    weekdaysLong: ["Svondo", "Muvhuro", "Chipiri", "Chitatu", "China", "Chishanu", "Mugovera"],
    weekdaysShort: ["Sv", "Mu", "Cp", "Ct", "Cn", "Cs", "Mg"],
  },
  zu: {
    monthsLong: [
      "Januwari",
      "Febhuwari",
      "Mashi",
      "Ephreli",
      "Meyi",
      "Juni",
      "Julayi",
      "Agasti",
      "Septhemba",
      "Okthoba",
      "Novemba",
      "Disemba",
    ],
    monthsShort: ["Jan", "Feb", "Mas", "Eph", "Mey", "Jun", "Jul", "Aga", "Sep", "Okt", "Nov", "Dis"],
    weekdaysLong: ["ISonto", "UMsombuluko", "ULwesibili", "ULwesithathu", "ULwesine", "ULwesihlanu", "UMgqibelo"],
    weekdaysShort: ["Son", "Mso", "Bil", "Tha", "Sin", "Hla", "Mgq"],
  },
};

function formatUiClock(totalMinutes, languageCode, locale) {
  if (!Number.isFinite(totalMinutes)) return "";
  const normalized = ((Math.round(totalMinutes) % 1440) + 1440) % 1440;
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  if (languageCode === "sn" || languageCode === "zu") {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }
  const date = new Date(2026, 0, 1, hours, minutes, 0, 0);
  return new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function formatUiMonthYear(date, languageCode, locale) {
  const custom = CUSTOM_LOCALE_LABELS[languageCode];
  if (custom) {
    return `${custom.monthsLong[date.getMonth()]}, ${date.getFullYear()}`;
  }
  return formatMonthYearHeadline(date, locale);
}

function formatUiMonthDay(date, languageCode, locale) {
  const custom = CUSTOM_LOCALE_LABELS[languageCode];
  if (custom) {
    return `${custom.monthsLong[date.getMonth()]} ${date.getDate()}`;
  }
  return formatMonthDay(date, locale);
}

function formatUiWeekRange(anchorDate, languageCode, locale) {
  const custom = CUSTOM_LOCALE_LABELS[languageCode];
  if (custom) {
    const week = buildWeekDates(anchorDate);
    const start = week[0];
    const end = week[6];
    if (!start || !end) return "";
    const sameMonth =
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth();
    if (sameMonth) {
      return `${start.getDate()}-${end.getDate()} ${custom.monthsLong[start.getMonth()]} ${start.getFullYear()}`;
    }
    return `${start.getDate()} ${custom.monthsShort[start.getMonth()]} - ${end.getDate()} ${custom.monthsShort[end.getMonth()]} ${end.getFullYear()}`;
  }
  return formatWeekRangeHeadline(anchorDate, locale);
}

// ---- tiny helpers (no dependencies) ----
function pad2(n) {
  return String(n).padStart(2, "0");
}

function minutesFromHHMM(hhmm) {
  const [h, m] = String(hhmm).split(":").map((x) => Number(x));
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

function hourLabel(h, locale = undefined) {
  const date = new Date(2026, 0, 1, h === 24 ? 0 : h, 0, 0, 0);
  try {
    return new Intl.DateTimeFormat(locale, { hour: "numeric" }).format(date);
  } catch {
    if (h === 0 || h === 24) return "12 AM";
    if (h < 12) return `${h} AM`;
    if (h === 12) return "12 PM";
    return `${h - 12} PM`;
  }
}

function formatMonthYear(d, locale = undefined) {
  try {
    return d.toLocaleString(locale, { month: "long", year: "numeric" });
  } catch {
    return "March 2025";
  }
}

function formatMonthYearHeadline(d, locale = undefined) {
  const raw = formatMonthYear(d, locale);
  if (!raw) return raw;
  if (raw.includes(",")) return raw;

  const parts = raw.split(" ");
  if (parts.length >= 2) {
    const year = parts.pop();
    return `${parts.join(" ")}, ${year}`;
  }

  return raw;
}

function formatWeekRangeHeadline(anchorDate, locale = undefined) {
  const week = buildWeekDates(anchorDate);
  const start = week[0];
  const end = week[6];
  if (!start || !end) return "";

  const sameMonth =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth();
  const sameYear = start.getFullYear() === end.getFullYear();

  try {
    if (sameMonth) {
      const monthYear = start.toLocaleDateString(locale, {
        month: "long",
        year: "numeric",
      });
      return `${start.getDate()}-${end.getDate()} ${monthYear}`;
    }

    if (sameYear) {
      const startLabel = start.toLocaleDateString(locale, { month: "short", day: "numeric" });
      const endLabel = end.toLocaleDateString(locale, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return `${startLabel} - ${endLabel}`;
    }

    const startLabel = start.toLocaleDateString(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const endLabel = end.toLocaleDateString(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${startLabel} - ${endLabel}`;
  } catch {
    return `${start.toDateString()} - ${end.toDateString()}`;
  }
}

function formatMonthDay(d, locale = undefined) {
  try {
    return d.toLocaleDateString(locale, { month: "long", day: "numeric" });
  } catch {
    return "March 23";
  }
}

function isSameMonth(a, b) {
  return (
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth()
  );
}

function shiftMonth(date, offset, today = new Date()) {
  const next = new Date(date);
  next.setDate(1);
  next.setMonth(next.getMonth() + Number(offset || 0));
  if (isSameMonth(next, today)) {
    next.setDate(today.getDate());
  }
  return next;
}

function shiftWeek(date, offset) {
  const next = new Date(date);
  next.setDate(next.getDate() + Number(offset || 0) * 7);
  return next;
}

function parseClockToMinutes(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const hhmm = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (hhmm) {
    return Number(hhmm[1]) * 60 + Number(hhmm[2]);
  }

  const upper = raw.toUpperCase();
  const ampm = upper.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/);
  if (!ampm) return null;

  let hours = Number(ampm[1]);
  const minutes = Number(ampm[2] || 0);
  if (ampm[3] === "AM") hours = hours === 12 ? 0 : hours;
  if (ampm[3] === "PM") hours = hours === 12 ? 12 : hours + 12;
  return hours * 60 + minutes;
}

function formatClockMinutes(totalMinutes, locale = undefined) {
  if (!Number.isFinite(totalMinutes)) return "";

  const minutesInDay = 24 * 60;
  const normalized = ((Math.round(totalMinutes) % minutesInDay) + minutesInDay) % minutesInDay;
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  const date = new Date(2026, 0, 1, hours, minutes, 0, 0);

  try {
    return new Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  } catch {
    const suffix = hours >= 12 ? "PM" : "AM";
    const hour12 = hours % 12 || 12;
    return `${hour12}:${String(minutes).padStart(2, "0")} ${suffix}`;
  }
}

function formatAppointmentStart(value, locale = undefined) {
  const totalMinutes = parseClockToMinutes(value);
  return totalMinutes == null ? String(value || "") : formatClockMinutes(totalMinutes, locale);
}

function formatAppointmentEnd(value, durationMinutes, locale = undefined) {
  const totalMinutes = parseClockToMinutes(value);
  if (totalMinutes == null) return "";
  return formatClockMinutes(totalMinutes + Number(durationMinutes || 0), locale);
}

// Utility: stable YYYY-MM-DD key
const dateKey = (d) => {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function buildWeekDates(anchorDate) {
  // Sunday-start week (matches your blueprint labels)
  const d = new Date(anchorDate);
  const day = d.getDay(); // 0..6
  const start = new Date(d);
  start.setDate(d.getDate() - day);

  const out = [];
  for (let i = 0; i < 7; i++) {
    const x = new Date(start);
    x.setDate(start.getDate() + i);
    out.push(x);
  }
  return out;
}

function buildAppointmentQueryRange(anchorDate, viewMode) {
  const anchor = new Date(anchorDate);
  anchor.setHours(0, 0, 0, 0);

  let start = new Date(anchor);
  let end = new Date(anchor);
  if (viewMode === "week") {
    start = buildWeekDates(anchor)[0];
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(end.getDate() + 7);
  } else if (viewMode === "month") {
    start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
  } else if (viewMode === "list") {
    end.setDate(end.getDate() + 3);
  } else {
    end.setDate(end.getDate() + 1);
  }

  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

function isPermissionDeniedMessage(message) {
  return /permission denied/i.test(String(message || ""));
}

function resolveDoctorIdCandidates(...values) {
  const out = [];
  const seen = new Set();
  for (const value of values) {
    const id = String(value || "").trim();
    if (
      !id ||
      seen.has(id) ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id)
    ) {
      continue;
    }
    seen.add(id);
    out.push(id);
  }
  return out;
}

function pickPrimaryFacilityName(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return "";

  const sorted = [...list].sort((a, b) => {
    const aPrimary = a?.is_primary ? 1 : 0;
    const bPrimary = b?.is_primary ? 1 : 0;
    if (aPrimary !== bPrimary) return bPrimary - aPrimary;

    const aSort = Number.isFinite(Number(a?.sort_order)) ? Number(a.sort_order) : Number.MAX_SAFE_INTEGER;
    const bSort = Number.isFinite(Number(b?.sort_order)) ? Number(b.sort_order) : Number.MAX_SAFE_INTEGER;
    if (aSort !== bSort) return aSort - bSort;

    const aUpdated = Date.parse(String(a?.updated_at || "")) || 0;
    const bUpdated = Date.parse(String(b?.updated_at || "")) || 0;
    return bUpdated - aUpdated;
  });

  const first = sorted.find((row) => String(row?.facility_name || row?.name || "").trim()) || null;
  return String(first?.facility_name || first?.name || "").trim();
}

function isPendingAppointment(appt) {
  return String(appt?.status || appt?.payload?.status || "").toLowerCase() === "pending";
}

function getAppointmentMode(appt) {
  const explicit = String(
    appt?.mode ||
      appt?.visitType ||
      appt?.payload?.mode ||
      appt?.payload?.visitType ||
      ""
  )
    .trim()
    .toLowerCase();
  if (explicit === "evisit" || explicit === "inperson") return explicit;

  const consultationType = String(
    appt?.consultation_type || appt?.payload?.consultation_type || ""
  )
    .trim()
    .toLowerCase();
  if (consultationType === "private") return "evisit";
  if (consultationType && consultationType !== "private") return "inperson";

  const modeText = [
    appt?.type,
    appt?.kind,
    appt?.category,
    appt?.subtitle,
    appt?.serviceName,
    appt?.serviceLabel,
    appt?.payload?.subtitle,
    appt?.payload?.serviceName,
    appt?.payload?.serviceLabel,
    appt?.payload?.booked_service_name,
    appt?.appointment_type,
    appt?.payload?.appointment_type,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean)
    .join(" ");

  if (
    modeText.includes("e-visit") ||
    modeText.includes("evisit") ||
    modeText.includes("video") ||
    modeText.includes("virtual") ||
    modeText.includes("tele")
  ) {
    return "evisit";
  }

  return "inperson";
}

function getAppointmentIcon(appt) {
  if (isPendingAppointment(appt)) return ICON_APPOINTMENT_PENDING;
  return getAppointmentMode(appt) === "evisit"
    ? ICON_APPOINTMENT_EVISIT
    : ICON_APPOINTMENT_LOCATION;
}

function getAppointmentSubtitleLabel(appt, labels = {}) {
  const base = String(appt?.subtitle || "").trim();
  const status = String(appt?.status || appt?.payload?.status || "").trim().toLowerCase();
  if (status === "missed") {
    return base ? `${base} • ${labels.missed || "Missed"}` : labels.missed || "Missed";
  }
  return base;
}

function getApptSkin(appt) {
  const isPending = isPendingAppointment(appt);
  const isEVisit = getAppointmentMode(appt) === "evisit";

  if (isPending) {
    return {
      bg: "rgb(var(--afrimed-appointment-card-pending-bg))",
      border: "rgba(var(--afrimed-appointment-card-pending-border), 0.16)",
      dashed: true,
      accent: "rgb(var(--afrimed-appointment-card-pending-accent))",
    };
  }

  if (isEVisit) {
    return {
      bg: "rgb(var(--afrimed-appointment-card-evisit-bg))",
      border: null,
      dashed: false,
      accent: "rgb(var(--afrimed-appointment-card-evisit-accent))",
    };
  }

  return {
    bg: "rgb(var(--afrimed-appointment-card-default-bg))",
    border: null,
    dashed: false,
    accent: "rgb(var(--afrimed-appointment-card-default-accent))",
  };
}

export function ApptCard({ appt, onOpen, style, className = "", heightPx, missedLabel = "Missed", as = "button", detailLine, ...rest }) {
  const skin = getApptSkin(appt);
  const appointmentIcon = getAppointmentIcon(appt);

  // Compact rules (tuned to your rail/card typography)
  const isTiny = typeof heightPx === "number" && heightPx < 44;   // e.g. 15–20 mins
  const isCompact = typeof heightPx === "number" && heightPx < 64; // e.g. 30 mins
  const hideSupportingRow = typeof heightPx === "number" && heightPx < 28;

  const avatarSize = isTiny ? 24 : isCompact ? 32 : 44;
  const padY = isTiny ? "py-0.5" : isCompact ? "py-2" : "py-3";
  const gapClass = isTiny ? "gap-2" : "gap-3";
  const nameClass = isTiny ? "text-body-small" : "text-[16px] leading-6 tracking-[-0.48px]";
  const subtitleClass = isTiny ? "text-body-small" : "text-[14px] leading-5 tracking-[-0.336px]";
  const durationClass = isTiny ? "text-body-small" : "text-[11px] leading-4 tracking-[-0.264px]";
  const iconSizeClass = isTiny ? "h-4 w-4" : "h-5 w-5";

return (
    <Card
        as={as}
        {...(as === "button" ? { type: "button", onClick: onOpen } : {})}
        {...rest}
        style={{
            ...(style || {}),
            backgroundColor: skin.bg,
            borderColor: skin.border || undefined,
        }}
        className={[
            "w-full",
            "px-4",
            padY,
            `flex items-center ${gapClass}`,
            "text-left",
            "overflow-hidden",
            skin.border ? "border" : "",
            skin.dashed ? "border-dashed" : "",
            "transition-colors duration-150",
            className,
        ].join(" ")}
        >
      <AvatarThumb68
        src={appt.avatar}
        alt={appt.patient_name}
        role="patient"
        shape="circle"
        className="shrink-0"
        style={{ width: avatarSize, height: avatarSize }}
      />

      <div className="min-w-0 flex-1">
        <div className={["truncate text-[rgb(var(--afrimed-appointment-card-on))]", nameClass].join(" ")}>
          {appt.patient_name}
        </div>

        {!hideSupportingRow && (
          <div className={["mt-0.5 flex items-center gap-1.5 text-[rgb(var(--afrimed-appointment-card-on-muted))]", subtitleClass].join(" ")}>
            <img src={appointmentIcon} alt="" className={`${iconSizeClass} opacity-90`} draggable="false" />
            <span className="truncate">
              {getAppointmentSubtitleLabel(appt, {
                missed: missedLabel,
              })}
            </span>
          </div>
        )}
        {detailLine ? (
          <div className="mt-0.5 truncate text-[11px] leading-4 text-[rgb(var(--afrimed-appointment-card-on-muted))]">
            {detailLine}
          </div>
        ) : null}
      </div>

      <div className="shrink-0 text-right">
        <div className={["text-[rgb(var(--afrimed-appointment-card-on-muted))]", durationClass].join(" ")}>
          {appt.duration_mins} mins
        </div>
      </div>
    </Card>
  );
}

export default function DoctorAppointmentsDaily() {
  const nav = useNavigate();
  const location = useLocation();
  const routeStartedAtRef = React.useRef(performance.now());
  const firstUsefulPaintedRef = React.useRef(false);
  const { languageCode, t } = useLanguage();
  const localeCode = React.useMemo(() => resolveUiLocale(languageCode), [languageCode]);
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const doctorId =
    auth?.user?.id || auth?.profile?.user_id || auth?.profile?.id || null;

  const defaultFacilityName = t(
    "appointments.doctorIndex.defaultFacilityName",
    "Primary facility not set"
  );
  const dayLabels = React.useMemo(() => {
    const custom = CUSTOM_LOCALE_LABELS[languageCode];
    const baseSunday = new Date(2026, 0, 4);
    return Array.from({ length: 7 }, (_, index) => {
      const next = new Date(baseSunday);
      next.setDate(baseSunday.getDate() + index);
      if (custom) return custom.weekdaysShort[index];
      try {
        return new Intl.DateTimeFormat(localeCode, { weekday: "narrow" }).format(next);
      } catch {
        return ["S", "M", "T", "W", "T", "F", "S"][index];
      }
    });
  }, [localeCode]);

  const [facilityName, setFacilityName] = React.useState(defaultFacilityName);
  const [appointmentsByDate, setAppointmentsByDate] = React.useState({});
  const [appointmentSnapshotsById, setAppointmentSnapshotsById] = React.useState({});
  const [apptsLoading, setApptsLoading] = React.useState(true);
  const [apptsError, setApptsError] = React.useState("");
  const [apptsPermissionBlocked, setApptsPermissionBlocked] = React.useState(false);
  const [appointmentsRefreshKey, setAppointmentsRefreshKey] = React.useState(0);
  const [resolvedDoctorIds, setResolvedDoctorIds] = React.useState(() =>
    resolveDoctorIdCandidates(
      doctorId,
      auth?.profile?.user_id,
      auth?.profile?.id,
      auth?.profile?.doctor_id,
    )
  );
  const [doctorIdentityReady, setDoctorIdentityReady] = React.useState(false);
  const [date, setDate] = React.useState(() => {
    const candidate = String(
      location.state?.focusDate || location.state?.rescheduledDate || ""
    ).trim();
    if (candidate) {
      const parsed = new Date(`${candidate}T00:00:00`);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  });
  const [rescheduleSnackbar, setRescheduleSnackbar] = React.useState(() => {
    const appointmentId = String(location.state?.rescheduledAppointmentId || "").trim();
    const rescheduledDate = String(location.state?.rescheduledDate || "").trim();
    if (!location.state?.justRescheduled || !appointmentId || !rescheduledDate) return null;
    return { appointmentId, rescheduledDate };
  });
  const [cancelSnackbar, setCancelSnackbar] = React.useState(() => {
    const appointmentId = String(location.state?.cancelledAppointmentId || "").trim();
    const cancelledSnapshot = location.state?.cancelledSnapshot || null;
    if (!location.state?.justCancelled || !appointmentId) return null;
    return { appointmentId, cancelledSnapshot };
  });
  const [viewMode, setViewMode] = React.useState(() => {
    const preferred = String(location.state?.preferredView || "").toLowerCase();
    if (["day", "week", "month", "list"].includes(preferred)) return preferred;
    const qs = new URLSearchParams(location.search || "");
    const candidate = String(qs.get("view") || "").toLowerCase();
    return ["day", "week", "month", "list"].includes(candidate) ? candidate : "day";
  });
  const appointmentQueryRange = React.useMemo(
    () => buildAppointmentQueryRange(date, viewMode),
    [date, viewMode]
  );

  React.useEffect(() => {
    let alive = true;
    const startedAt = performance.now();

    async function resolveIdentity() {
      const candidates = resolveDoctorIdCandidates(
        doctorId,
        auth?.profile?.user_id,
        auth?.profile?.id,
        auth?.profile?.doctor_id,
      );
      if (!doctorId) {
        if (alive) {
          setResolvedDoctorIds([]);
          setDoctorIdentityReady(true);
        }
        return;
      }

      try {
        if (candidates.length > 0) {
          const { data: ownProfile } = await supabase
            .from("doctor_profiles")
            .select("id,user_id")
            .in("user_id", candidates)
            .limit(1)
            .maybeSingle();
          if (ownProfile?.id) candidates.push(String(ownProfile.id).trim());
          if (ownProfile?.user_id) candidates.push(String(ownProfile.user_id).trim());
        }
      } catch {
        // The authenticated user id remains the canonical fallback.
      }

      if (!alive) return;
      const nextIds = resolveDoctorIdCandidates(...candidates);
      setResolvedDoctorIds(nextIds);
      setDoctorIdentityReady(true);
      recordStagingPerformance("doctor_appointments_identity_ready", {
        durationMs: Math.round(performance.now() - startedAt),
        routeDurationMs: Math.round(performance.now() - routeStartedAtRef.current),
        candidateCount: nextIds.length,
        profileLookupRequestCount: candidates.length > 0 ? 1 : 0,
      });
    }

    setDoctorIdentityReady(false);
    void resolveIdentity();
    return () => {
      alive = false;
    };
  }, [auth?.profile?.doctor_id, auth?.profile?.id, auth?.profile?.user_id, doctorId]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateFacilityName() {
      if (!doctorIdentityReady) return;
      if (!doctorId || resolvedDoctorIds.length === 0) {
        if (alive) setFacilityName(defaultFacilityName);
        return;
      }

      const startedAt = performance.now();
      try {
        const { data, error } = await supabase
          .from("doctor_consultancies")
          .select("id,facility_name,is_primary,sort_order,updated_at")
          .in("doctor_id", resolvedDoctorIds)
          .order("sort_order", { ascending: true })
          .order("updated_at", { ascending: false });

        if (!alive) return;

        if (!error && Array.isArray(data) && data.length > 0) {
          const dbName = pickPrimaryFacilityName(data);
          if (dbName) {
            setFacilityName(dbName);
            recordStagingPerformance("doctor_appointments_facility_ready", {
              durationMs: Math.round(performance.now() - startedAt),
              routeDurationMs: Math.round(performance.now() - routeStartedAtRef.current),
            });
            return;
          }
        }
      } catch {
        // fallback handled below
      }

      const localRows = readDoctorConsultancies(doctorId);
      const localName = pickPrimaryFacilityName(localRows);
      if (!alive) return;
      setFacilityName(localName || defaultFacilityName);
      recordStagingPerformance("doctor_appointments_facility_ready", {
        durationMs: Math.round(performance.now() - startedAt),
        routeDurationMs: Math.round(performance.now() - routeStartedAtRef.current),
        source: localName ? "local" : "fallback",
      });
    }

    hydrateFacilityName();
    return () => {
      alive = false;
    };
  }, [defaultFacilityName, doctorId, doctorIdentityReady, resolvedDoctorIds]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateAppointments() {
      if (!doctorIdentityReady) return;
      if (!doctorId || resolvedDoctorIds.length === 0) {
        if (!alive) return;
        setAppointmentsByDate({});
        setAppointmentSnapshotsById({});
        setApptsError("");
        setApptsLoading(false);
        return;
      }

      try {
        const startedAt = performance.now();
        let canonicalCommitted = false;
        setApptsLoading(true);
        setApptsError("");
        setApptsPermissionBlocked(false);

        const { byDate, snapshotsById, error } = await loadDoctorAppointmentsFeed({
          doctorId: resolvedDoctorIds,
          facilityName,
          rangeStart: appointmentQueryRange.start,
          rangeEnd: appointmentQueryRange.end,
          onCanonicalFeed: ({ byDate: canonicalByDate, snapshotsById: canonicalSnapshots, trace }) => {
            if (!alive) return;
            canonicalCommitted = true;
            setAppointmentsByDate(canonicalByDate || {});
            setAppointmentSnapshotsById(canonicalSnapshots || {});
            setApptsLoading(false);
            recordStagingPerformance("doctor_appointments_canonical_committed", {
              durationMs: Math.round(performance.now() - startedAt),
              routeDurationMs: Math.round(performance.now() - routeStartedAtRef.current),
              appointmentRequestCount: trace?.appointmentRequestCount ?? 1,
              rowCount: trace?.rowCount ?? 0,
              viewMode,
            });
          },
        });

        if (!alive) return;
        if (error) {
          setAppointmentsByDate({});
          setAppointmentSnapshotsById({});
          const msg =
            error?.message ||
            t("appointments.doctorIndex.errorLoad", "Unable to load appointments.");
          if (isPermissionDeniedMessage(msg)) {
            setApptsPermissionBlocked(true);
            setApptsError("");
          } else {
            setApptsError(msg);
          }
          return;
        }

        setAppointmentsByDate(byDate || {});
        setAppointmentSnapshotsById(snapshotsById || {});
        recordStagingPerformance("doctor_appointments_enrichment_complete", {
          durationMs: Math.round(performance.now() - startedAt),
          routeDurationMs: Math.round(performance.now() - routeStartedAtRef.current),
          canonicalCommitted,
          rowCount: Object.keys(snapshotsById || {}).length,
          viewMode,
        });
      } catch (error) {
        if (!alive) return;
        setAppointmentsByDate({});
        setAppointmentSnapshotsById({});
        const msg =
          error?.message ||
          t("appointments.doctorIndex.errorLoad", "Unable to load appointments.");
        if (isPermissionDeniedMessage(msg)) {
          setApptsPermissionBlocked(true);
          setApptsError("");
        } else {
          setApptsError(msg);
        }
      } finally {
        if (alive) setApptsLoading(false);
      }
    }

    hydrateAppointments();
    return () => {
      alive = false;
    };
  }, [
    appointmentQueryRange.end,
    appointmentQueryRange.start,
    appointmentsRefreshKey,
    doctorId,
    doctorIdentityReady,
    resolvedDoctorIds,
    t,
    viewMode,
  ]);

  React.useEffect(() => {
    function handleAppointmentsChanged() {
      setAppointmentsRefreshKey((value) => value + 1);
    }

    window.addEventListener("afrimed:appointments:changed", handleAppointmentsChanged);
    return () => {
      window.removeEventListener("afrimed:appointments:changed", handleAppointmentsChanged);
    };
  }, []);

  const selectedKey = dateKey(date);

  // Header toggle (Calendar vs List)
  const [menuOpen, setMenuOpen] = React.useState(false);
  const calendarMenuRef = React.useRef(null);
  React.useEffect(() => {
    const qs = new URLSearchParams(location.search || "");
    const candidate = String(qs.get("view") || "").toLowerCase();
    if (!["day", "week", "month", "list"].includes(candidate)) return;
    setViewMode(candidate);
  }, [location.search]);

  React.useEffect(() => {
    const candidate = String(
      location.state?.focusDate || location.state?.rescheduledDate || ""
    ).trim();
    if (!candidate) return;
    const parsed = new Date(`${candidate}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return;
    setDate(parsed);
  }, [location.state?.focusDate, location.state?.rescheduledDate]);

  React.useEffect(() => {
    const preferred = String(location.state?.preferredView || "").toLowerCase();
    if (!["day", "week", "month", "list"].includes(preferred)) return;
    setViewMode(preferred);
  }, [location.state?.preferredView]);

  const applyViewMode = React.useCallback(
    (nextMode) => {
      const safeMode = ["day", "week", "month", "list"].includes(nextMode)
        ? nextMode
        : "day";
      setViewMode(safeMode);

      const qs = new URLSearchParams(location.search || "");
      if (safeMode === "day") qs.delete("view");
      else qs.set("view", safeMode);

      const search = qs.toString();
      nav(
        {
          pathname: location.pathname,
          search: search ? `?${search}` : "",
        },
        { replace: true }
      );
    },
    [location.pathname, location.search, nav]
  );

  React.useEffect(() => {
    if (!menuOpen) return undefined;

    function handlePointerDown(event) {
      if (calendarMenuRef.current?.contains(event.target)) return;
      setMenuOpen(false);
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") setMenuOpen(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen]);

  const week = React.useMemo(() => buildWeekDates(date), [date]);
  const stepMonth = React.useCallback((offset) => {
    setDate((current) => shiftMonth(current, offset, new Date()));
  }, []);
  const stepWeek = React.useCallback((offset) => {
    setDate((current) => shiftWeek(current, offset));
  }, []);
  const dateHeadline = React.useMemo(() => {
    if (viewMode === "week") return formatUiWeekRange(date, languageCode, localeCode);
    return formatUiMonthYear(date, languageCode, localeCode);
  }, [date, languageCode, localeCode, viewMode]);
  
  const appts = appointmentsByDate[selectedKey] || [];
  const totalLoadedAppointments = React.useMemo(
    () =>
      Object.values(appointmentsByDate || {}).reduce(
        (sum, rows) => sum + (Array.isArray(rows) ? rows.length : 0),
        0,
      ),
    [appointmentsByDate],
  );
  const showFolaAppointmentsGuidance =
    !apptsLoading && !apptsPermissionBlocked && !apptsError && totalLoadedAppointments === 0;

  React.useEffect(() => {
    if (apptsLoading || firstUsefulPaintedRef.current) return undefined;
    const frame = window.requestAnimationFrame(() => {
      if (firstUsefulPaintedRef.current) return;
      if (!document.querySelector('[data-afrimed-doctor-appointments-ready="true"]')) return;
      firstUsefulPaintedRef.current = true;
      recordStagingPerformance("doctor_appointments_first_useful_painted", {
        routeDurationMs: Math.round(performance.now() - routeStartedAtRef.current),
        rowCount: totalLoadedAppointments,
        viewMode,
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [apptsLoading, totalLoadedAppointments, viewMode]);

// ✅ Scroll-depth cue (mobile): show a subtle top fade when timeline is scrolled
const [timelineScrolled, setTimelineScrolled] = React.useState(false);

const onTimelineScroll = React.useCallback((e) => {
  const top = e.currentTarget?.scrollTop ?? 0;
  // tiny hysteresis avoids flicker
  const next = top > 6;
  setTimelineScrolled((prev) => (prev === next ? prev : next));
}, []);

    function openAppointmentDetails(a) {
    const payload =
      appointmentSnapshotsById?.[String(a?.id || "")] ||
      a?.payload || {
        appointmentId: a?.id,
        id: a?.id,
        facilityName,
        when: {
          dateISO: a?.dateISO || selectedKey,
          start: a?.start || "",
          duration_mins: Number(a?.duration_mins || 15),
        },
        patient: {
          name: a?.patient_name || t("appointments.doctorIndex.patientFallback", "Patient"),
          avatar_url: a?.avatar || "/icons/circular-avatar-placeholder.svg",
          age: a?.patient_age ?? null,
          sex: a?.patient_sex ?? null,
        },
        mode: getAppointmentMode(a),
        status: a?.status || "scheduled",
      };

    nav(`/doctors/appointments/${a.id}`, { state: payload });
  }

  function parseTimeToMinutes(t) {
  // expects formats like "8:00 AM", "08:30", "14:15"
  if (!t || typeof t !== "string") return null;

  const s = t.trim().toUpperCase();

  // 12h match
  const m12 = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/);
  if (m12) {
    let hh = Number(m12[1]);
    const mm = Number(m12[2] ?? 0);
    const ap = m12[3];
    if (ap === "AM") hh = hh === 12 ? 0 : hh;
    if (ap === "PM") hh = hh === 12 ? 12 : hh + 12;
    return hh * 60 + mm;
  }

  // 24h match
  const m24 = s.match(/^(\d{1,2})(?::(\d{2}))$/);
  if (m24) {
    const hh = Number(m24[1]);
    const mm = Number(m24[2] ?? 0);
    return hh * 60 + mm;
  }

  return null;
}

// ---- "today" detection (real) ----
const isSelectedToday =
  date && new Date(date).toDateString() === new Date().toDateString();

const DEMO_MARKER = false;

// Marker can render if we're on real today OR in demo mode
const markerEnabled = DEMO_MARKER || isSelectedToday;

// Single source of truth for "now"
const nowHour = markerEnabled ? (DEMO_MARKER ? 9 : new Date().getHours()) : null;
const nowMin  = markerEnabled ? (DEMO_MARKER ? 5 : new Date().getMinutes()) : null;

// Clamp marker to visible timeline range (✅ single source of truth)
const isNowInRange =
  markerEnabled &&
  nowHour != null &&
  nowHour >= MIN_HOUR &&
  nowHour <= MAX_HOUR; // MAX_HOUR should be 23 for full day

// Hours array used by BOTH Day + Week spines
const hours = React.useMemo(
  () => Array.from({ length: (MAX_HOUR - MIN_HOUR + 2) }, (_, i) => MIN_HOUR + i), // +2 => includes 24 tick
  [MIN_HOUR, MAX_HOUR]
);

const TOP_GUTTER_PX = 14; // prevents first label/top line clipping

// --- Vertical scroll container for the timeline (mobile + desktop if needed)
const timelineScrollRef = React.useRef(null);

// px from top of timeline we want to land on (gives breathing room above marker/cards)
const SCROLL_ANCHOR_PAD = 120;

const scrollTimelineTo = React.useCallback((targetTopPx) => {
  const el = timelineScrollRef.current;
  if (!el) return;
  const next = Math.max(0, targetTopPx - SCROLL_ANCHOR_PAD);
  el.scrollTop = next;
}, []);

// Scroll to "now" (demo or real) when entering day/week
React.useEffect(() => {
  if (!(viewMode === "day" || viewMode === "week")) return;

  // If marker is active, prefer it
  if (markerEnabled && isNowInRange && nowHour != null && nowMin != null) {
    const minsFromStart = (nowHour - MIN_HOUR) * 60 + nowMin;
    const topPx = TOP_GUTTER_PX + minsFromStart * PX_PER_MIN;
    scrollTimelineTo(topPx);
    return;
  }

  // Otherwise, scroll to first appointment in the selected day (if present)
  const k = dateKey(date);
  const list =
    viewMode === "week"
      ? (appointmentsByDate[k] || [])
      : (appts || []);

  const starts = list
    .map((a) => apptStartMins(a))
    .filter((v) => v != null && Number.isFinite(v))
    .sort((a, b) => a - b);

  if (starts.length) {
    const topPx = TOP_GUTTER_PX + (starts[0] - MIN_HOUR * 60) * PX_PER_MIN;
    scrollTimelineTo(topPx);
  } else {
    // fallback: start of day
    scrollTimelineTo(TOP_GUTTER_PX);
  }
}, [
  viewMode,
  date,
  markerEnabled,
  isNowInRange,
  nowHour,
  nowMin,
  MIN_HOUR,
  PX_PER_MIN,
  TOP_GUTTER_PX,
  appts,
  appointmentsByDate,
  scrollTimelineTo,
]);

// Start minutes parser used by BOTH Day + Week
const apptStartMins = (a) => {
  const t = a?.start;
  const mins24 = minutesFromHHMM(t);     // "08:30"
  if (mins24 != null && Number.isFinite(mins24)) return mins24;

  const minsAny = parseTimeToMinutes(t); // "08:30 AM"
  if (minsAny != null && Number.isFinite(minsAny)) return minsAny;

  return null;
};

  return (
    <div className="w-full bg-[rgb(var(--clr-surface-container-low))]">
      <main className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <div className="w-full max-w-[720px] md:max-w-[1008px] mx-auto">
          <div className="flex items-center justify-between gap-4 px-0 py-2">
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-[20px] leading-7 tracking-[-0.48px] text-on-surface">
                {facilityName}
              </h1>
              <div className="mt-0.5 flex items-center gap-2">
                <p className="text-title-medium text-on-surface-var">
                {dateHeadline}
              </p>
                {viewMode === "month" || viewMode === "week" ? (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => (viewMode === "week" ? stepWeek(-1) : stepMonth(-1))}
                      aria-label={
                        viewMode === "week"
                          ? t("appointments.doctorIndex.weekNav.previous", "Previous week")
                          : t("appointments.doctorIndex.monthNav.previous", "Previous month")
                      }
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-on-surface-var transition-colors hover:bg-[rgba(255,255,255,0.04)] hover:text-on-surface"
                    >
                      <span
                        className="h-5 w-5 bg-current"
                        style={{
                          maskImage: `url(${ICON_MONTH_BACK})`,
                          WebkitMaskImage: `url(${ICON_MONTH_BACK})`,
                          maskRepeat: "no-repeat",
                          WebkitMaskRepeat: "no-repeat",
                          maskPosition: "center",
                          WebkitMaskPosition: "center",
                          maskSize: "contain",
                          WebkitMaskSize: "contain",
                        }}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => (viewMode === "week" ? stepWeek(1) : stepMonth(1))}
                      aria-label={
                        viewMode === "week"
                          ? t("appointments.doctorIndex.weekNav.next", "Next week")
                          : t("appointments.doctorIndex.monthNav.next", "Next month")
                      }
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-on-surface-var transition-colors hover:bg-[rgba(255,255,255,0.04)] hover:text-on-surface"
                    >
                      <span
                        className="h-5 w-5 bg-current"
                        style={{
                          maskImage: `url(${ICON_MONTH_FORWARD})`,
                          WebkitMaskImage: `url(${ICON_MONTH_FORWARD})`,
                          maskRepeat: "no-repeat",
                          WebkitMaskRepeat: "no-repeat",
                          maskPosition: "center",
                          WebkitMaskPosition: "center",
                          maskSize: "contain",
                          WebkitMaskSize: "contain",
                        }}
                      />
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            <div ref={calendarMenuRef} className="relative shrink-0">
              <button
                type="button"
                onClick={() => setMenuOpen((value) => !value)}
                aria-label={t("appointments.doctorIndex.viewMode.label", "View mode")}
                className="inline-flex h-10 w-10 items-center justify-center text-on-surface-var transition-colors hover:text-on-surface"
              >
                <img
                  src={ICON_CALENDAR}
                  alt=""
                  className="h-6 w-6 opacity-80"
                  draggable="false"
                />
              </button>

              {menuOpen && (
                <div className="absolute right-0 mt-2 w-[220px] overflow-hidden rounded-[14px] border border-[rgba(91,107,134,0.24)] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_18px_36px_rgba(2,6,23,0.24)] z-50">
                  {[
                    {
                      key: "day",
                      label: t("appointments.doctorIndex.viewMode.day", "Day"),
                      icon: ICON_CALENDAR_TOGGLE,
                    },
                    {
                      key: "week",
                      label: t("appointments.doctorIndex.viewMode.week", "Week"),
                      icon: ICON_WEEK_VIEW,
                    },
                    {
                      key: "month",
                      label: t("appointments.doctorIndex.viewMode.month", "Month"),
                      icon: ICON_MONTH_VIEW,
                    },
                    {
                      key: "list",
                      label: t("appointments.doctorIndex.viewMode.list", "List"),
                      icon: ICON_LISTS,
                    },
                  ].map((item, idx) => {
                    const isActive = viewMode === item.key;

                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => {
                          applyViewMode(item.key);
                          setMenuOpen(false);
                        }}
                        className={[
                          "w-full px-4 py-3 text-left",
                          isActive ? "bg-[rgba(26,140,255,0.12)]" : "bg-transparent",
                          idx < 3 ? "border-b border-[rgba(91,107,134,0.16)]" : "",
                        ].join(" ")}
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={item.icon}
                            alt=""
                            className={["h-6 w-6", isActive ? "opacity-100" : "opacity-70"].join(" ")}
                            draggable="false"
                          />
                          <div className="text-body-large text-on-surface">{item.label}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
    
    {/* ✅ Unified strip: DoW always, date chips only for non-month */}
    <div className="mt-3 border-b border-[rgba(91,107,134,0.22)] pb-2">
    {/* DoW row */}
    <div
        className={[
        "grid grid-cols-7 gap-0",
        "text-label-medium text-on-surface-var",
        "select-none",
        "whitespace-nowrap",
        ].join(" ")}
    >
        {dayLabels.map((d, i) => (
        <div key={`dow-${i}-${d}`} className="h-6 flex items-center justify-center">
            {d}
        </div>
        ))}
    </div>

    {/* Date chips row (ONLY Week/Day modes) */}
    {viewMode !== "month" && (
        <div
          className="mt-2 grid grid-cols-7 gap-0"
        >
        {week.map((d) => {
            const dayNum = d.getDate();
            const active = dateKey(d) === dateKey(date);

            return (
            <div
                key={`${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(dayNum)}`}
                className="flex items-center justify-center"
            >
                <button
                type="button"
                onClick={() => setDate(d)}
                className={[
                    "h-[26px] w-[26px] rounded-full",
                    "flex items-center justify-center",
                    active
                    ? "bg-[rgb(var(--schemes-critical))] text-[rgb(var(--afrimed-on-primary))]"
                    : "text-on-surface-var",
                ].join(" ")}
                aria-label={t("appointments.doctorIndex.selectDay", "Select day {day}", {
                  day: dayNum,
                })}
                >
                <span className="text-label-large">{dayNum}</span>
                </button>
            </div>
            );
        })}
        </div>
    )}
    </div>

    {apptsLoading ? (
      <Card className="mt-4 p-4 text-body-medium text-on-surface-var">
        {t("appointments.doctorIndex.loading", "Loading appointments...")}
      </Card>
    ) : null}

    {!apptsLoading && apptsPermissionBlocked ? (
      <Card className="mt-6 p-6 text-center">
        <div className="text-title-large text-on-surface">
          {t("appointments.doctorIndex.emptyTitle", "No appointments to display")}
        </div>
        <div className="mt-1 text-body-large text-on-surface-var">
          {t(
            "appointments.doctorIndex.emptyBody",
            "Your inbox is currently empty. Check back later!"
          )}
        </div>
      </Card>
    ) : null}

    {!apptsLoading && !apptsPermissionBlocked && apptsError ? (
      <Card className="mt-4 p-4 text-body-medium text-on-surface-var">
        {apptsError}
      </Card>
    ) : null}

    {showFolaAppointmentsGuidance ? (
      <div className="mt-4">
        <FolaOnboardingCard role="doctor" placement="doctorAppointments" />
      </div>
    ) : null}

    {/* View content (band from weekday strip down) */}
    <div
      className="mt-0 -mx-4 px-4 bg-[rgb(var(--clr-surface))] relative"
      data-afrimed-doctor-appointments-ready={!apptsLoading ? "true" : undefined}
    >
    {/* ✅ top shadow/gradient cue (pinned to panel width, not scroll width) */}
    <div
        className={[
        "pointer-events-none absolute left-0 right-0 top-0 z-30",
        "h-4 bg-gradient-to-b from-[rgba(15,23,42,0.08)] via-[rgba(15,23,42,0.04)] to-transparent",
        "transition-opacity duration-200",
        timelineScrolled ? "opacity-100" : "opacity-0",
        ].join(" ")}
    />

    {/* ✅ The timeline scrolls vertically inside this container */}
    <div
        ref={timelineScrollRef}
        onScroll={onTimelineScroll}
        className={[
        "relative overflow-y-auto overscroll-y-contain overscroll-x-none no-scrollbar",
        "overflow-x-hidden touch-pan-y",
        "[-webkit-overflow-scrolling:touch]",
        "pb-24 lg:pb-[160px]",
        "h-[calc(100dvh-260px)]",
        ].join(" ")}
    >
        {/* ✅ Scroll depth indicator (clipped + no hard “tab” edge) */}
        <div
        className={[
            "pointer-events-none absolute left-0 right-0 top-0 z-30",
            "h-6",                          // a bit taller = softer fade
            "overflow-hidden",              // ✅ clip the fade
            "transition-opacity duration-200",
            timelineScrolled ? "opacity-100" : "opacity-0",
        ].join(" ")}
        >
        {/* Only gradient (no solid base) */}
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(15,23,42,0.10)] via-[rgba(15,23,42,0.04)] to-transparent blur-[1.25px]" />
        </div>

        {viewMode === "list" ? (
        <section className="pt-6">
            {(() => {
            // LIST VIEW — grouped by day from DB hydration map
            const base = new Date(date);
            base.setHours(0, 0, 0, 0);

            // Build 3 days: today + next 2 days
                const days = [0, 1, 2].map((offset) => {
                const d = new Date(base);
                d.setDate(base.getDate() + offset);
                const k = dateKey(d);
                const items = appointmentsByDate?.[k] || [];

                return {
                date: d,
                key: k,
                label: (() => {
                  const custom = CUSTOM_LOCALE_LABELS[languageCode];
                  if (custom) {
                    return `${custom.weekdaysLong[d.getDay()]}, ${custom.monthsLong[d.getMonth()]} ${d.getDate()}`;
                  }
                  return d.toLocaleDateString(localeCode, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  });
                })(),
                items,
                };
            });

            const DividerRow = () => (
            <div className="mx-4 md:mx-5">
                <div className="h-px bg-[rgba(91,107,134,0.24)] w-full" />
            </div>
            );

            return (
                <div className="space-y-8 pb-8 pt-1 md:space-y-10">
                {days.map((group, gIdx) => {
                    const isEmpty = group.items.length === 0;

                    return (
                    <div key={`list-group-${group.key}`}>
                        {/* Day header */}
                        <div className="px-4 pb-5 text-title-medium text-on-surface md:px-5 md:pb-4">
                        {group.label}
                        </div>

                        {/* Free schedule state */}
                        {isEmpty ? (
                            <>
                            <div className="flex items-center justify-between gap-4 px-4 py-5 text-on-surface-var md:px-5">
                            <span className="text-body-large">{t("appointments.doctorIndex.list.freeSchedule", "Free schedule")}</span>
                            <span className="shrink-0 text-body-large text-on-surface-var">
                              {t("appointments.doctorIndex.list.allDay", "All day")}
                            </span>
                            </div>
                            <DividerRow />
                            </>
                        ) : (
                            <>
                            {group.items.map((a, idx) => {
                                const isPending = isPendingAppointment(a);
                                const appointmentIcon = getAppointmentIcon(a);
                                const startLabel = formatUiClock(parseClockToMinutes(a.start), languageCode, localeCode) || formatAppointmentStart(a.start, localeCode);
                                const endLabel = formatUiClock(
                                  parseClockToMinutes(a.start) == null
                                    ? null
                                    : parseClockToMinutes(a.start) + Number(a.duration_mins || 0),
                                  languageCode,
                                  localeCode,
                                ) || formatAppointmentEnd(a.start, a.duration_mins, localeCode);

                                return (
                                <React.Fragment key={a.id}>
                                    <button
                                    type="button"
                                    onClick={() => openAppointmentDetails(a)}
                                    className="flex min-h-[118px] w-full items-center gap-4 px-4 py-3 text-left transition-colors duration-150 hover:bg-[rgba(255,255,255,0.02)] md:min-h-[104px] md:px-5"
                                    >
                                    <AvatarThumb68
                                        src={a.avatar}
                                        alt={a.patient_name}
                                        role="patient"
                                        shape="circle"
                                        className="shrink-0"
                                        style={{ width: 44, height: 44 }}
                                    />

                                    <div className="min-w-0 flex-1">
                                        <div
                                        className={[
                                            "truncate text-body-large",
                                            isPending ? "text-on-surface-var opacity-60" : "text-on-surface",
                                        ].join(" ")}
                                        >
                                        {a.patient_name}
                                        </div>

                                        <div
                                        className={[
                                            "mt-1 flex items-center gap-2.5 text-body-medium",
                                            isPending ? "text-on-surface-var opacity-60" : "text-on-surface-var",
                                        ].join(" ")}
                                        >
                                        <img
                                            src={appointmentIcon}
                                            alt=""
                                            className="h-6 w-6 shrink-0 opacity-80"
                                            draggable="false"
                                        />
                                        <span className="truncate">
                                          {getAppointmentSubtitleLabel(a, {
                                            missed: t("appointments.details.status.missed", "Missed"),
                                          })}
                                        </span>
                                        </div>
                                    </div>

                                    <div
                                    className={[
                                        "w-[96px] shrink-0 text-right text-body-large md:w-[112px]",
                                        isPending ? "text-on-surface-var opacity-60" : "text-on-surface-var",
                                    ].join(" ")}
                                    >
                                        <div>{startLabel}</div>
                                        <div>{endLabel || `${a.duration_mins} mins`}</div>
                                    </div>
                                    </button>

                                    <DividerRow />
                                </React.Fragment>
                                );
                            })}
                            </>
                        )}

                        {gIdx < days.length - 1 && <div className="h-px" />}
                    </div>
                    );
                })}
                </div>
            );
            })()}
        </section>

        ) : viewMode === "week" ? (
        <section className="pt-6">
            {/* WEEK VIEW */}
            {(() => {
            // ✅ single source of truth for selection
            const selectedKey = dateKey(date);

            return (
                <div className="relative overflow-x-auto overflow-y-hidden no-scrollbar -mr-4 pr-4 overscroll-x-contain">
                <div className="min-w-[980px]">
                    {/* Body (one timeline per day column) */}
                    <div className="relative">
                    {/* Left labels column (shared) */}
                    <div className="absolute left-0 top-0" style={{ width: TIME_RAIL_WIDTH }}>
                        {hours.map((h) => {
                        const y = (h - MIN_HOUR) * 60 * PX_PER_MIN;
                        return (
                            <div
                            key={`wk-hour-${h}`}
                            className="absolute left-0 text-body-medium text-on-surface-var"
                            style={{ top: TOP_GUTTER_PX + y - 9 }}
                            >
                            {formatUiClock(h * 60, languageCode, localeCode)}
                            </div>
                        );
                        })}
                    </div>

                    {/* ✅ full grid columns with separators */}
                    <div className="grid grid-cols-7 gap-0 border-l border-[rgba(91,107,134,0.16)]" style={{ marginLeft: TIME_RAIL_WIDTH }}>
                        {week.map((d) => {
                        const k = dateKey(d);
                        const isSelectedDay = k === selectedKey;

                        const dayAppts = appointmentsByDate[k] || [];

                        const startOfRangeMins = MIN_HOUR * 60;
                        const endOfRangeMins = (MAX_HOUR + 1) * 60;

                        const positioned = dayAppts
                            .map((a) => {
                            const startMins = apptStartMins(a);
                            if (startMins == null) return null;

                            const dur = Number(a.duration_mins || 0);
                            const topMins = startMins - startOfRangeMins;

                            const clampedTopMins = Math.max(
                                0,
                                Math.min(topMins, endOfRangeMins - startOfRangeMins)
                            );

                            const clampedDurMins = Math.max(
                                12,
                                Math.min(dur, endOfRangeMins - startMins)
                            );

                            return {
                                appt: a,
                                topPx: clampedTopMins * PX_PER_MIN,
                                heightPx: clampedDurMins * PX_PER_MIN,
                            };
                            })
                            .filter(Boolean);

                        // ✅ marker: v.01 simple = only on selected day
                        const markerOk =
                            markerEnabled &&
                            isSelectedDay &&
                            isNowInRange &&
                            nowHour != null &&
                            nowMin != null;

                        const nowMins = markerOk
                            ? (nowHour * 60 + nowMin - startOfRangeMins) * PX_PER_MIN
                            : null;

                        return (
                            <div
                            key={`wk-col-${k}`}
                            className="relative border-r border-[rgba(91,107,134,0.16)]"
                            style={{ height: TIMELINE_HEIGHT }}
                            >
                            {/* 1) grid lines */}
                            <div className="absolute inset-0 z-0">
                                {hours.map((h) => {
                                const y = (h - MIN_HOUR) * 60 * PX_PER_MIN;
                                return (
                                    <div
                                    key={`wk-div-${k}-${h}`}
                                    className="absolute left-0 right-0"
                                    style={{ top: TOP_GUTTER_PX + y }}
                                    >
                                    <div className="h-px bg-[rgba(91,107,134,0.16)] w-full" />
                                    </div>
                                );
                                })}
                            </div>

                            {/* 2) events */}
                            <div className="absolute inset-0 z-10">
                                {positioned.map(({ appt, topPx, heightPx }) => (
                                <div
                                    key={appt.id}
                                    className="absolute left-0 right-0"
                                    style={{ top: TOP_GUTTER_PX + topPx, height: heightPx }}
                                >
                                    <ApptCard
                                    appt={appt}
                                    heightPx={heightPx}
                                    onOpen={() => openAppointmentDetails(appt)}
                                    missedLabel={t("appointments.details.status.missed", "Missed")}
                                    className="h-full"
                                    />
                                </div>
                                ))}
                            </div>

                            {/* 3) marker */}
                            {nowMins != null && (
                                <div className="absolute inset-0 z-20 pointer-events-none">
                                <div
                                    className="absolute left-0 right-0"
                                    style={{ top: TOP_GUTTER_PX + nowMins }}
                                >
                                    <div className="h-[1.5px] w-full bg-[rgb(var(--schemes-critical))]" />
                                </div>
                                <div
                                    className="absolute left-0 h-3 w-[2px] rounded-full bg-[rgb(var(--schemes-critical))]"
                                    style={{ top: TOP_GUTTER_PX + nowMins - 6 }}
                                />
                                </div>
                            )}

                            {/* Selected day emphasis */}
                            {isSelectedDay && (
                                <div className="absolute inset-0 pointer-events-none ring-1 ring-[rgba(24,50,119,0.10)]" />
                            )}
                            </div>
                        );
                        })}
                    </div>
                    </div>
                </div>
                </div>
            );
            })()}

        <div className="h-8" />
            </section>
        ) : viewMode === "month" ? (
            <section className="pt-3">
                {/* MONTH VIEW */}
                {(() => {
                const year = date.getFullYear();
                const month = date.getMonth(); // 0-11

                const start = new Date(year, month, 1);
                const end = new Date(year, month + 1, 0);
                const daysInMonth = end.getDate();

                // Sunday-start
                const startDow = start.getDay(); // 0=Sun

                const cells = [];
                for (let i = 0; i < startDow; i++) cells.push(null);
                for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
                while (cells.length % 7 !== 0) cells.push(null);

                const isSameDay = (a, b) =>
                    a &&
                    b &&
                    a.getFullYear() === b.getFullYear() &&
                    a.getMonth() === b.getMonth() &&
                    a.getDate() === b.getDate();

                // counts per day from DB hydration map
                const getCounts = (d) => {
                    if (!d) return { appointment: 0, evisit: 0, pending: 0, total: 0 };
                    const k = dateKey(d);
                    const items = appointmentsByDate?.[k] || [];

                    let appointment = 0,
                    evisit = 0,
                    pending = 0;

                    for (const a of items) {
                    if (isPendingAppointment(a)) {
                        pending++;
                        continue;
                    }

                    if (getAppointmentMode(a) === "evisit") {
                        evisit++;
                    } else {
                        appointment++;
                    }
                    }

                    const total = appointment + evisit + pending;
                    return { appointment, evisit, pending, total };
                };

                return (
                    <div className="mt-3">
                    <div className="mt-3 w-full max-w-[1100px] mx-auto overflow-hidden rounded-[18px] bg-[rgb(var(--clr-surface))]">
                        <div className="grid grid-cols-7 border-l border-t border-[rgba(91,107,134,0.16)]">
                        {cells.map((d, idx) => {
                            const isWeekend = d ? d.getDay() === 0 || d.getDay() === 6 : false;
                            const active = d ? isSameDay(d, date) : false;
                            const counts = getCounts(d);

                            return (
                            <button
                                key={`m-cell-${idx}`}
                                type="button"
                                disabled={!d}
                                onClick={() => d && setDate(d)}
                                className={[
                                "relative",
                                "min-h-[86px] md:min-h-[112px] lg:min-h-[128px]",
                                "border-b border-r border-[rgba(91,107,134,0.16)]",
                                "flex flex-col items-start justify-start",
                                "px-3 py-3 text-left md:px-4",
                                isWeekend ? "text-on-surface-var" : "text-on-surface",
                                !d ? "pointer-events-none" : "",
                                d ? "transition-colors duration-150 hover:bg-[rgba(255,255,255,0.02)]" : "bg-[rgba(255,255,255,0.01)]",
                                ].join(" ")}
                                aria-label={d ? `Select day ${d.getDate()}` : undefined}
                            >
                                {/* Date chip */}
                                <div
                                className={[
                                    "flex h-8 w-8 items-center justify-center rounded-full md:h-9 md:w-9",
                                    active
                                    ? "bg-[rgb(var(--schemes-critical))] text-[rgb(var(--afrimed-on-primary))]"
                                    : "bg-transparent text-current",
                                ].join(" ")}
                                >
                                <span className={active ? "text-label-large" : "text-label-large opacity-90"}>
                                    {d ? d.getDate() : ""}
                                </span>
                                </div>

                                {/* Indicators (Dashboard style: mixed pills / dots) */}
                                <div className="mt-4 flex items-center gap-1.5">
                                {counts.total === 0 ? (
                                    <div className="h-[8px]" />
                                ) : (
                                    <>
                                    {/* Appointment */}
                                    <div
                                        className={[
                                        "h-[8px] rounded-full transition-all",
                                        counts.appointment
                                            ? "w-[18px] opacity-100"
                                            : "w-[8px] opacity-30",
                                        counts.appointment
                                          ? "bg-[rgb(var(--afrimed-appointment-card-default-accent))]"
                                          : "bg-[rgb(var(--afrimed-appointment-card-default-accent))]",
                                        ].join(" ")}
                                    />

                                    {/* Virtual (Tertiary) — boosted visibility */}
                                    <div
                                        className={[
                                            "h-[8px] rounded-full",
                                            counts.evisit ? "w-[14px]" : "w-[8px] opacity-40",
                                            "bg-[rgb(var(--afrimed-appointment-card-evisit-accent))]",
                                        ].join(" ")}
                                        />

                                    {/* Pending */}
                                    <div
                                        className={[
                                        "h-[8px] rounded-full transition-all",
                                        counts.pending
                                            ? "w-[18px] opacity-90"
                                            : "w-[8px] opacity-30",
                                        "bg-[rgb(var(--afrimed-appointment-card-pending-accent))]",
                                        ].join(" ")}
                                    />
                                    </>
                                )}
                                </div>
                            </button>
                            );
                        })}
                        </div>
                    </div>
                    </div>
                );
                })()}
            </section>
            ) : (
            <section className="pt-6">
            {/* DAY VIEW (your existing branch) */}
            {(() => {
              // ✅ keep everything in-scope for the Day renderer
              const hours = Array.from(
                { length: (MAX_HOUR - MIN_HOUR + 2) }, // +2 includes the 24:00 tick
                (_, i) => MIN_HOUR + i
              );

              const startOfRangeMins = MIN_HOUR * 60;
              const endOfRangeMins = (MAX_HOUR + 1) * 60;

              const dayAppts = appts || [];

              const apptStartMins = (a) => {
                const t = a?.start;
                const mins24 = minutesFromHHMM(t);
                if (mins24 != null && Number.isFinite(mins24)) return mins24;

                const minsAny = parseTimeToMinutes(t);
                if (minsAny != null && Number.isFinite(minsAny)) return minsAny;

                return null;
              };

              const positioned = dayAppts
                .map((a) => {
                  const startMins = apptStartMins(a);
                  if (startMins == null) return null;

                  const dur = Number(a.duration_mins || 0);
                  const topMins = startMins - startOfRangeMins;

                  const clampedTopMins = Math.max(
                    0,
                    Math.min(topMins, endOfRangeMins - startOfRangeMins)
                  );

                  const clampedDurMins = Math.max(
                    12,
                    Math.min(dur, endOfRangeMins - startMins)
                  );

                  return {
                    appt: a,
                    topPx: clampedTopMins * PX_PER_MIN,
                    heightPx: clampedDurMins * PX_PER_MIN,
                  };
                })
                .filter(Boolean);

              return (
                <div className="relative">
                  {/* Left labels column */}
                  <div className="absolute left-0 top-0" style={{ width: TIME_RAIL_WIDTH }}>
                    {hours.map((h) => {
                      const y = (h - MIN_HOUR) * 60 * PX_PER_MIN;
                      return (
                        <div
                          key={h}
                          className="absolute left-0 text-body-medium text-on-surface-var"
                          style={{ top: TOP_GUTTER_PX + y - 9 }}
                        >
                          {formatUiClock(h * 60, languageCode, localeCode)}
                        </div>
                      );
                    })}
                  </div>

                  {/* Timeline canvas */}
                  <div className="relative" style={{ marginLeft: TIME_RAIL_WIDTH, height: TIMELINE_HEIGHT }}>
                    {/* 1) GRID LAYER (z-0) */}
                    <div className="absolute inset-0 z-0">
                      {hours.map((h) => {
                        const y = (h - MIN_HOUR) * 60 * PX_PER_MIN;
                        return (
                          <div key={h} className="absolute left-0 right-0" style={{ top: TOP_GUTTER_PX + y }}>
                            <div className="h-px w-full bg-[rgba(91,107,134,0.16)]" />
                          </div>
                        );
                      })}
                    </div>

                    {/* 2) EVENTS LAYER (z-10) */}
                    <div className="absolute inset-0 z-10 pr-4">
                      {positioned.map(({ appt, topPx, heightPx }) => (
                        <div
                          key={appt.id}
                          className="absolute left-0 right-0"
                          style={{ top: TOP_GUTTER_PX + topPx, height: heightPx }}
                        >
                          <ApptCard
                            appt={appt}
                            heightPx={heightPx}
                            onOpen={() => openAppointmentDetails(appt)}
                            missedLabel={t("appointments.details.status.missed", "Missed")}
                            className="h-full"
                          />
                        </div>
                      ))}
                    </div>

                    {/* 3) MARKER LAYER (z-20) */}
                    {isNowInRange && nowHour != null && nowMin != null &&
                      (() => {
                        const minsFromStart = (nowHour - MIN_HOUR) * 60 + nowMin;
                        const markerTopPx = minsFromStart * PX_PER_MIN;

                        return (
                          <>
                            <div
                              className="absolute left-0 right-0 z-20 pointer-events-none"
                              style={{ top: TOP_GUTTER_PX + markerTopPx }}
                            >
                              <div className="h-[1.5px] w-full bg-[rgb(var(--schemes-critical))] transition-opacity duration-200" />
                            </div>

                            <div
                              className="absolute left-0 z-20 h-3 w-[2px] rounded-full bg-[rgb(var(--schemes-critical))] pointer-events-none"
                              style={{ top: TOP_GUTTER_PX + markerTopPx - 6 }}
                            />
                          </>
                        );
                      })()}
                  </div>
                </div>
              );
            })()}
          </section>
        )}
      </div>
        </div>
    </div>

        {rescheduleSnackbar ? (
          <div className="fixed inset-x-0 bottom-[calc(84px+env(safe-area-inset-bottom))] z-40 px-6">
            <div className="mx-auto flex w-full max-w-[720px] items-start gap-4 rounded-[10px] bg-[rgb(var(--clr-inverse-surface,4,8,18))] px-4 py-3 text-[rgb(var(--clr-inverse-on-surface,240,241,243))] shadow-[0_12px_36px_-6px_rgba(26,27,35,0.24)]">
              <div className="min-w-0 flex-1 text-body-medium">
                {t(
                  "appointments.doctorIndex.snackbar.rescheduled",
                  "The appointment was moved to {date}. Pending for patient acceptance.",
                  {
                    date: formatUiMonthDay(
                      new Date(`${rescheduleSnackbar.rescheduledDate}T00:00:00`),
                      languageCode,
                      localeCode
                    ),
                  }
                )}
              </div>

              <button
                type="button"
                onClick={() => {
                  openAppointmentDetails({
                    id: rescheduleSnackbar.appointmentId,
                    ...(appointmentSnapshotsById?.[rescheduleSnackbar.appointmentId] || {}),
                  });
                  setRescheduleSnackbar(null);
                }}
                className="shrink-0 text-label-large text-[rgb(var(--afrimed-primary))]"
              >
                {t("appointments.doctorIndex.snackbar.review", "Review")}
              </button>
            </div>
          </div>
        ) : null}

        {cancelSnackbar ? (
          <div className="fixed inset-x-0 bottom-[calc(84px+env(safe-area-inset-bottom))] z-40 px-6">
            <div className="mx-auto flex h-12 w-full max-w-[720px] items-center gap-4 rounded-[10px] bg-[rgb(var(--clr-inverse-surface,4,8,18))] px-4 py-1 text-[rgb(var(--clr-inverse-on-surface,240,241,243))] shadow-[0_12px_36px_-6px_rgba(26,27,35,0.24)]">
              <div className="min-w-0 flex-1 text-body-medium">
                {t("appointments.doctorIndex.snackbar.cancelled", "Appointment cancelled")}
              </div>

              <button
                type="button"
                onClick={() => {
                  openAppointmentDetails({
                    id: cancelSnackbar.appointmentId,
                    ...(cancelSnackbar.cancelledSnapshot || {}),
                  });
                  setCancelSnackbar(null);
                }}
                className="shrink-0 text-label-large text-[rgb(var(--afrimed-primary))]"
              >
                {t("appointments.doctorIndex.snackbar.review", "Review")}
              </button>
            </div>
          </div>
        ) : null}

        {/* Bottom quick nav removed (dropdown only per blueprint) */}
        <div className="h-10" />
      </main>
    </div>
  );
}
